import { useEffect, useState } from 'react';
import { useCharacterStore } from '@/state/characterStore';
import { useUIStore } from '@/state/uiStore';
import { useSkillStore, type SkillTemplate } from '@/state/skillStore';
import { Skill } from '@/network/RestApi';
import { getSkillIcon } from '@/data/skillIconRegistry';
import { ui } from './theme';
import { CloseButton } from './CloseButton';

const ELEMENT_LABELS: Record<number, string> = {
  0: 'Vật lý',
  1: 'Lửa',
  2: 'Băng',
  3: 'Sét',
  4: 'Đất',
  5: 'Phong',
  6: 'Thánh',
  7: 'Hắc ám',
};

type SkillKind = 'damage' | 'heal' | 'buff' | 'toggle';

function inferKind(s: SkillTemplate): SkillKind {
  if (s.isToggle) return 'toggle';
  if (s.buffDurationMs > 0 && s.baseFlat === 0 && s.multiplier <= 1 && s.statScale === 0) return 'buff';
  if (s.code.includes('heal') || (s.branch === 'C' && s.baseFlat > 0 && s.multiplier <= 1)) return 'heal';
  return 'damage';
}

const KIND_LABELS: Record<SkillKind, string> = {
  damage: 'Tấn công',
  heal: 'Hồi phục',
  buff: 'Buff / Hỗ trợ',
  toggle: 'Bật/Tắt',
};

const KIND_COLORS: Record<SkillKind, string> = {
  damage: '#ff6b6b',
  heal: '#6bff8e',
  buff: '#ffd700',
  toggle: '#c78dff',
};

function SkillDetail({ s, lv, onClose }: { s: SkillTemplate; lv: number; onClose: () => void }) {
  const curLv = Math.max(1, lv || 1);
  const flatCur = s.baseFlat + s.growthFlat * (curLv - 1);
  const flatNext = lv > 0 && lv < s.maxLevel ? s.baseFlat + s.growthFlat * lv : null;
  const kind = inferKind(s);
  const row = (label: string, value: React.ReactNode) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', borderBottom: '1px dashed #1f2933', fontSize: 12 }}>
      <span style={{ color: '#9ec5fe' }}>{label}</span>
      <span style={{ color: '#e8eef7' }}>{value}</span>
    </div>
  );

  const isDamage = kind === 'damage';
  const isHeal = kind === 'heal';
  const hasDot = s.dotPerSec > 0 && s.dotDurationMs > 0;
  const hasCc = !!s.ccKind && s.ccChance > 0;

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
      onClick={onClose}
    >
      <div style={{ width: 340, ...ui.panel }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          {(() => {
            const icon = getSkillIcon(s.code, s.class);
            return icon
              ? <img src={icon} alt="" draggable={false} style={{ width: 48, height: 48, objectFit: 'contain', imageRendering: 'pixelated', flexShrink: 0 }} />
              : null;
          })()}
          <strong style={{ flex: 1 }}>{s.name}</strong>
          <CloseButton onClick={onClose} />
        </div>
        {s.description && (
          <div style={{ fontSize: 11, color: '#b0b8c8', marginBottom: 8, fontStyle: 'italic' }}>{s.description}</div>
        )}
        <div style={{ fontSize: 11, color: '#888', marginBottom: 8 }}>{s.code}</div>
        {row('Cấp độ', `${lv}/${s.maxLevel}`)}
        {row('Yêu cầu Lv', s.levelReq)}
        {row('Loại', <span style={{ color: KIND_COLORS[kind] }}>{s.isMaster ? 'Master · ' : ''}{KIND_LABELS[kind]}{s.isMagic ? ' (Phép)' : ''}</span>)}
        {s.element > 0 && row('Nguyên tố', ELEMENT_LABELS[s.element] ?? s.element)}
        {row('Mana', s.manaCost)}
        {s.manaDrainPerS > 0 && row('Mana/giây', s.manaDrainPerS.toFixed(0))}
        {row('Hồi chiêu', `${(s.cooldownMs / 1000).toFixed(1)}s`)}

        {(isDamage || isHeal) && s.multiplier > 0 && row(isHeal ? 'Hệ số hồi phục' : 'Hệ số sát thương', `x${s.multiplier.toFixed(2)}`)}
        {(isDamage || isHeal) && s.statScale > 0 && row('Stat scale', s.statScale.toFixed(2))}
        {(isDamage || isHeal) && s.baseFlat > 0 && row(
          isHeal ? 'Lượng hồi cơ bản' : 'Sát thương cộng thêm',
          flatNext != null ? `${flatCur.toFixed(0)} → ${flatNext.toFixed(0)}` : flatCur.toFixed(0),
        )}
        {(isDamage || isHeal) && s.growthFlat > 0 && row('Tăng mỗi cấp', `+${s.growthFlat}`)}

        {s.aoeRadiusM > 0 && row('Bán kính AoE', `${s.aoeRadiusM}m`)}
        {(s.rangeM ?? 0) > 0 && row('Tầm đánh', `${s.rangeM}m`)}
        {s.maxTargets > 0 && row('Mục tiêu tối đa', s.maxTargets)}
        {s.defensePenPct > 0 && row('Xuyên giáp', `${(s.defensePenPct * 100).toFixed(0)}%`)}

        {s.buffDurationMs > 0 && row('Thời gian buff', `${(s.buffDurationMs / 1000).toFixed(1)}s`)}
        {hasDot && row('DOT', `${s.dotPerSec.toFixed(0)}/s trong ${(s.dotDurationMs / 1000).toFixed(1)}s`)}
        {hasCc && row('CC', `${s.ccKind} ${(s.ccChance * 100).toFixed(0)}% (${(s.ccDurationMs / 1000).toFixed(1)}s)`)}
        {s.slowPct > 0 && row('Làm chậm', `${(s.slowPct * 100).toFixed(0)}%`)}
      </div>
    </div>
  );
}

export function SkillTreePanel() {
  const ch = useCharacterStore((s) => s.current);
  const skillPoints = useCharacterStore((s) => s.skillPoints);
  const setSkillPoints = useCharacterStore((s) => s.setSkillPoints);
  const templates = useSkillStore((s) => s.templates);
  const fetchSkills = useSkillStore((s) => s.fetch);
  const learned = useSkillStore((s) => s.learned);
  const fetchLearned = useSkillStore((s) => s.fetchLearned);
  const setLearnedLevel = useSkillStore((s) => s.setLearnedLevel);
  const close = () => useUIStore.getState().set('skill', false);
  const showToast = useUIStore((s) => s.showToast);
  const [detail, setDetail] = useState<SkillTemplate | null>(null);

  useEffect(() => { void fetchSkills(); }, [fetchSkills]);

  useEffect(() => {
    if (ch) void fetchLearned(ch.id);
  }, [ch, fetchLearned]);

  if (!ch) return null;
  const list = templates.filter((s) => s.class === ch.class);

  const spendSkillPoint = () => setSkillPoints(Math.max(0, skillPoints - 1));

  const learn = async (s: SkillTemplate) => {
    try {
      await Skill.learn(ch.id, { skillId: s.id });
      setLearnedLevel(s.id, 1);
      spendSkillPoint();
      showToast(`Đã học ${s.name}`);
    } catch (e) { showToast((e as Error).message, 'error'); }
  };
  const upgrade = async (s: SkillTemplate) => {
    try {
      await Skill.levelUp(ch.id, { skillId: s.id });
      setLearnedLevel(s.id, (learned[s.id] ?? 0) + 1);
      spendSkillPoint();
      showToast(`${s.name} +1`);
    } catch (e) { showToast((e as Error).message, 'error'); }
  };

  // Server uses branch codes 'A'/'B'/'C'; map to friendly labels and order.
  const branchOrder: { code: string; label: string }[] = [
    { code: 'A', label: 'Single Target' },
    { code: 'B', label: 'AoE' },
    { code: 'C', label: 'Support' },
    { code: 'master', label: 'Master' },
  ];

  const classLabel = ch.class.charAt(0).toUpperCase() + ch.class.slice(1);

  return (
    <div
      style={{
        position: 'absolute',
        top: 70,
        right: 10,
        bottom: 10,
        width: 420,
        maxHeight: 'calc(100vh - 90px)',
        zIndex: 100,
        display: 'flex',
        flexDirection: 'column',
        ...ui.panel,
        padding: '22px 18px 18px',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 12,
          flexShrink: 0,
          borderBottom: '1px solid rgba(180,140,60,0.4)',
          paddingBottom: 10,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <strong style={{ fontSize: 16, color: '#fff5cc', letterSpacing: 1 }}>
            Cây Skill
          </strong>
          <span style={{ fontSize: 11, color: '#9ec5fe', textTransform: 'uppercase', letterSpacing: 1 }}>
            {classLabel}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div
            style={{
              fontSize: 11,
              color: '#ffd700',
              background: 'rgba(0,0,0,0.35)',
              border: '1px solid rgba(180,140,60,0.5)',
              padding: '4px 10px',
              borderRadius: 4,
              whiteSpace: 'nowrap',
            }}
          >
            SP: <strong>{skillPoints}</strong>
          </div>
          <CloseButton onClick={close} />
        </div>
      </div>

      {/* Body */}
      <div style={{ overflowY: 'auto', flex: 1, minHeight: 0, paddingRight: 4 }}>
        {branchOrder.map(({ code, label }) => {
          const items = list.filter((s) => (code === 'master' ? s.isMaster : s.branch === code && !s.isMaster));
          if (!items.length) return null;
          return (
            <div key={code} style={{ marginBottom: 14 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  color: '#4cd1ff',
                  fontSize: 11,
                  marginBottom: 6,
                  textTransform: 'uppercase',
                  letterSpacing: 1.5,
                  fontWeight: 700,
                }}
              >
                <span style={{ flex: '0 0 auto' }}>{label}</span>
                <span style={{ flex: 1, height: 1, background: 'linear-gradient(90deg, rgba(76,209,255,0.5), transparent)' }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {items.map((s) => {
                  const lv = learned[s.id] ?? 0;
                  const meetsLevel = ch.level >= s.levelReq;
                  const meetsMaster = !s.isMaster || ch.resetCount >= 100;
                  const meetsReq = meetsLevel && meetsMaster;
                  const lockedColor = !meetsReq ? '#ff6b6b' : '#9ec5fe';
                  const kind = inferKind(s);
                  const learned1 = lv > 0;
                  return (
                    <div
                      key={s.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        padding: '6px 8px',
                        background: learned1 ? 'rgba(76,209,255,0.06)' : 'rgba(0,0,0,0.25)',
                        border: `1px solid ${learned1 ? 'rgba(76,209,255,0.3)' : 'rgba(180,140,60,0.2)'}`,
                        borderRadius: 4,
                        cursor: 'pointer',
                        transition: 'background 0.15s',
                      }}
                      onClick={() => setDetail(s)}
                      title="Click để xem chi tiết"
                    >
                      {(() => {
                        const icon = getSkillIcon(s.code, s.class);
                        return (
                          <div
                            style={{
                              width: 40,
                              height: 40,
                              flexShrink: 0,
                              background: 'rgba(0,0,0,0.4)',
                              border: `1px solid ${learned1 ? '#4cd1ff' : 'rgba(180,140,60,0.4)'}`,
                              borderRadius: 3,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              boxShadow: learned1 ? '0 0 6px rgba(76,209,255,0.4)' : 'none',
                            }}
                          >
                            {icon ? (
                              <img
                                src={icon}
                                alt=""
                                draggable={false}
                                style={{
                                  width: 36,
                                  height: 36,
                                  objectFit: 'contain',
                                  imageRendering: 'pixelated',
                                  opacity: meetsReq ? 1 : 0.4,
                                  filter: meetsReq ? 'none' : 'grayscale(0.7)',
                                }}
                              />
                            ) : null}
                          </div>
                        );
                      })()}
                      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, gap: 2 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          <span
                            style={{
                              fontSize: 13,
                              fontWeight: 600,
                              color: meetsReq ? '#fff5cc' : '#888',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {s.name}
                          </span>
                          <span
                            style={{
                              fontSize: 10,
                              color: KIND_COLORS[kind],
                              background: 'rgba(0,0,0,0.4)',
                              padding: '1px 6px',
                              borderRadius: 8,
                              border: `1px solid ${KIND_COLORS[kind]}33`,
                            }}
                          >
                            {KIND_LABELS[kind]}
                          </span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 10 }}>
                          <span style={{ color: learned1 ? '#4cd1ff' : '#888' }}>
                            Lv <strong>{lv}</strong>/{s.maxLevel}
                          </span>
                          <span style={{ color: lockedColor }}>
                            • Yêu cầu Lv {s.levelReq}
                            {s.isMaster ? ` · Reset 100` : ''}
                          </span>
                        </div>
                      </div>
                      <span onClick={(e) => e.stopPropagation()} style={{ flexShrink: 0 }}>
                        {lv === 0 ? (
                          <button
                            style={{ ...ui.secondaryBtn, width: 64, height: 30, fontSize: 11 }}
                            onClick={() => learn(s)}
                            disabled={!meetsReq || skillPoints <= 0}
                          >
                            Học
                          </button>
                        ) : (
                          <button
                            style={{ ...ui.secondaryBtn, width: 36, height: 30, fontSize: 14 }}
                            onClick={() => upgrade(s)}
                            disabled={lv >= s.maxLevel || skillPoints <= 0}
                          >
                            +
                          </button>
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      {detail && <SkillDetail s={detail} lv={learned[detail.id] ?? 0} onClose={() => setDetail(null)} />}
    </div>
  );
}
